// ABOUTME: What one business's row in the Agents tree shows: its pending approvals (the row's
// badge), and once expanded its team roles with their live status. Every
// request names the business (X-Business-ID), so a business that isn't active reads its own.
import { useQuery } from '@tanstack/react-query'
import { api, inBusiness } from '@/api/client'
import { fetchRosterStatus, ROSTER_STATUS_KEY } from '@/api/agentRoles'

const REFETCH_MS = 60_000

export function useBusinessAgents(businessId: string, expanded: boolean) {
  const options = { refetchInterval: REFETCH_MS, retry: false } as const
  const pending = useQuery({
    queryKey: ['agent-approvals', 'count', businessId],
    queryFn: async () => {
      const res = await api.get<{ total: number }>(
        '/agent/approvals?limit=1',
        inBusiness(businessId)
      )
      return res?.total ?? 0
    },
    ...options,
  })
  const roster = useQuery({
    queryKey: [...ROSTER_STATUS_KEY, businessId],
    queryFn: () => fetchRosterStatus(businessId),
    enabled: expanded,
    ...options,
  })
  return {
    pending: pending.data ?? 0,
    roster: roster.data ?? [],
  }
}

/** Collapse state per business, kept in this browser; storage may be unavailable. */
const collapsedKey = (businessId: string) => `mf.agents.collapsed.${businessId}`

export function readCollapsed(businessId: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(collapsedKey(businessId))
    return stored === null ? fallback : stored === '1'
  } catch {
    return fallback
  }
}

export function writeCollapsed(businessId: string, collapsed: boolean): void {
  try {
    localStorage.setItem(collapsedKey(businessId), collapsed ? '1' : '0')
  } catch {
    // Not remembered in this browser; the tree still works.
  }
}
